import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import BarChartIcon from "@mui/icons-material/BarChart";
import { green } from "@mui/material/colors";

import { withTranslation } from "react-i18next";
import { debounce, isEmpty } from "lodash";
import { addDays, format } from "date-fns";

import fpToolAvailabilityService from "@plannel/services/fp/fp-tool-availability-service";
import fpCommonService from "@plannel/services/fp/fp-common-service";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import PeriodSearchGroup, { BUCKET_CASE } from "@plannel/pages/factory-plan/common/component/PeriodSearchGroup";
import FpGridSetting, {
  readHide, buildGroupColumnPair, REPORT_TREE_GRID_OPTIONS, buildReportStatusBar,
  applyColumnOrder, buildDayBucketPivotColumnDefs, consumePendingFocus
} from "@plannel/pages/factory-plan/common/grid";
import { buildXAxisSortMap } from "@plannel/pages/factory-plan/common/chart";
import { getFilterStore } from "@plannel/components/aggrid/FilterStatusPanel";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import SplitterContainer from "@plannel/components/SplitterContainer";
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import Snackbar from "@plannel/components/Snackbar";
import { DownloadButton } from "@plannel/components/ActionIconButton";
import ColorUtil from "@plannel/pages/utils/color-util";
import NumberUtil from "@plannel/utils/number-util";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old FP_SRH_RANGE.attr01Val(DB 단일 고정값) 이식 — PlanResult 파일럿에서 실제 확인된 값(9)을
// 그대로 재사용한다(fp-report-screen-guide.md §A-3c). old ToolAvailability.jsx의 로컬 fallback
// (14, `let fpSettings = { searchZone: 14 }`)이 아니라 그 실제 DB 값을 이식한다.
// TODO(MIG-T2): [toolavailability/ToolAvailability] 조회조건 세팅 3종(버킷 목록/검색필드 표시/
//   검색구간)의 테넌트별 커스터마이징 미이관.
//   old: search-option/bucket · search-option/srh-options · setting FP_SRH_RANGE를 각각 DB에서 조회
//   사유: 정적 구성(JSON 카탈로그/고정 카탈로그/상수)으로 전환 — 가이드 §A-3~§A-3d에서 결정
const SEARCH_ZONE_DAYS = 9;

// bucketCase 배정(가이드 §A-3 표에는 없던 예외를 이 화면에서 추가 발견 — 사람 확인 필요).
// ToolAvailabilityService#getToolAvailability가 JobChangeTimeInfoService와 동일하게
// `bucket.isHourBucket()`이면 빈 결과를 반환한다(백엔드 코드 확인). 가이드 §A-3 배정표의
// WITHOUT_HOUR 예외 2개(ResProdPlan/ResourceUtilization) + JobChangeTimeInfo(2026-08-11 추가)에
// 이 화면도 같은 근거로 추가해야 한다고 판단해 WITHOUT_HOUR를 쓴다.
const TOOL_AVAILABILITY_BUCKET_CASE = BUCKET_CASE.WITHOUT_HOUR;

const PARAM_DATE_FORMAT = "yyyyMMdd";

// 차트 고정 지표 5개 — old INDICATOR_CONFIG(ToolAvailabilityChart.jsx 대응 로직은
// ToolAvailability.jsx의 setChart에 있음) 그대로 이식. FP_MAX_CONSUME_RATE만 메인 차트의
// 우측(%) 축, 나머지는 좌측(개수) 축. FP_MAX_CONSUME_CNT/FP_MIN_REMAIN_CNT는 메인 차트에서 같은
// stack(old stack:'bar1')으로 쌓이고, FP_SUPPLY_CNT/FP_DISPOSAL_CNT는 서브 차트에서 각각 별도
// stack(old stack:'bar3'/'bar4', 즉 나란히 표시)으로 그려진다.
const MAIN_INDICATORS = ["FP_MAX_CONSUME_RATE", "FP_MAX_CONSUME_CNT", "FP_MIN_REMAIN_CNT"];
const SUB_INDICATORS = ["FP_SUPPLY_CNT", "FP_DISPOSAL_CNT"];
const INDICATOR_CONFIG = {
  FP_MAX_CONSUME_RATE: { type: "line", color: "#FC5F67", orientation: "right" },
  FP_MAX_CONSUME_CNT: { type: "bar", color: "#185FA5", orientation: "left", stackId: "toolAvailMain" },
  FP_MIN_REMAIN_CNT: { type: "bar", color: "#B5D4F4", orientation: "left", stackId: "toolAvailMain" },
  FP_SUPPLY_CNT: { type: "bar", color: "#247c3a", orientation: "left", stackId: "toolAvailSupply" },
  FP_DISPOSAL_CNT: { type: "bar", color: "#ce450f", orientation: "left", stackId: "toolAvailDisposal" }
};
// old isMust — FP_MAX_CONSUME_CNT/FP_MIN_REMAIN_CNT는 전부 0/null이어도 항상 시리즈를 그린다.
// 나머지(FP_MAX_CONSUME_RATE/FP_SUPPLY_CNT/FP_DISPOSAL_CNT)는 버킷 전체가 0/null이면 시리즈 자체를
// 만들지 않는다(old `hasValue` 체크).
const MUST_INDICATORS = new Set(["FP_MAX_CONSUME_CNT", "FP_MIN_REMAIN_CNT"]);

// 트리(행 그룹) 계층 — 공장 > 스테이지 > 툴 자원(leaf는 analysisIndicator, 5개 고정 지표).
// 백엔드 그룹핑 키(ToolAvailabilityCrosstabDto.merge의 GroupingLevel: PLANT/PLANT_STAGE/
// PLANT_STAGE_TOOL)가 이 3단과 정확히 일치하고, toolResourceCd가 그레인(그 값 하나당 항상
// analysisIndicator별 1행)이라 leaf로 내리지 않고 rowGroup 마지막 레벨로 둔다(§A-6b 판단 기준).
// old 컬럼정의도 plantGroup/stageGroup/resourceGroup 전부 mergeRule(Row Grouping 대상, Tree Data 아님).
const GROUP_FIELDS = ["plantCd", "plantNm", "stageCd", "stageNm", "toolResourceCd", "toolResourceNm"];

const PLANT_CD_COL_ID = "plantCdGrp";
const PLANT_NM_COL_ID = `PLANT_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const STAGE_CD_COL_ID = "stageCdGrp";
const STAGE_NM_COL_ID = `STAGE_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const TOOL_RESOURCE_CD_COL_ID = "toolResourceCdGrp";
const TOOL_RESOURCE_NM_COL_ID = `TOOL_RESOURCE_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;

const PLANT_NM_HIDE_KEY = "plantNmColsHide";
const STAGE_NM_HIDE_KEY = "stageNmColsHide";
const TOOL_RESOURCE_NM_HIDE_KEY = "toolResourceNmColsHide";

const ToolAvailability = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);

  const [versionState, setVersionState] = useState({
    planScope: "", planningDate: null, versionCd: "", plantCd: [], selectedVersion: null
  });
  const [periodState, setPeriodState] = useState({
    startDate: null, endDate: null, bucketType: "", isEntirePlanPeriod: true
  });
  // old InputField(type="multiSelect", toolResourceCd) — 선택형 조건이라 값이 바뀌는 즉시
  // 자동조회 대상이다(§A-9b). options는 planScope 변경 시 loadResources 대응으로 재조회하고,
  // 로드 즉시 old처럼 "전체 선택"으로 기본값을 채운다.
  const [toolResourceState, setToolResourceState] = useState({ selected: [], options: [] });
  const latestResourceReqRef = useRef(0);

  const [showSupplyDisposalChart, setShowSupplyDisposalChart] = useState(true);

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);

  const [chartLabels, setChartLabels] = useState([]);
  const [mainChartData, setMainChartData] = useState([]);
  const [mainChartSeries, setMainChartSeries] = useState([]);
  const [subChartData, setSubChartData] = useState([]);
  const [subChartSeries, setSubChartSeries] = useState([]);

  // 응답 순서 뒤바뀜 방지 — 조회(그리드)와 차트는 서로 다른 요청이라 가드도 2개(§0-8).
  const latestGridReqRef = useRef(0);
  const latestChartReqRef = useRef(0);
  // old prevChartParamsRef — 같은 (plant, stage, toolResource) 조합이면 차트를 다시 조회하지 않는다.
  const prevChartParamsRef = useRef(null);
  const pendingFocusRef = useRef(null);
  // columnDefs를 조회 응답 시점에 다시 만들어 넘기므로(§A-6), 최신 조회조건을 콜백에서 읽기 위한 ref.
  const searchStateRef = useRef({ versionState, periodState, toolResourceState });
  searchStateRef.current = { versionState, periodState, toolResourceState };

  const debouncedRetrieveRef = useRef();
  if (!debouncedRetrieveRef.current) {
    debouncedRetrieveRef.current = debounce(() => retrieveToolAvailability(), 200);
  }
  useEffect(() => () => debouncedRetrieveRef.current.cancel(), []);

  useEffect(() => () => getFilterStore(viewName).clear(), [viewName]);

  const defaultGridMemo = useMemo(() => {
    const base = FpGridSetting({ title, viewName });
    return {
      ...base,
      defaultColDef: { ...base.defaultColDef, editable: false },
      ...REPORT_TREE_GRID_OPTIONS,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // old displayCallback — 코드 자체를 항상 i18n 번역해서 보여준다(SUM 여부와 무관, ResourceUtilization
  // 의 analysisIndicator 컬럼과 동일 패턴).
  const analysisIndicatorFormatter = (params) => t(params.value);

  // old styleCallback(setCrossTabGridLayoutAndOptions) — zeroText:' '(0값은 빈 셀) +
  // FP_MAX_CONSUME_RATE 행만 '%' suffix. 그리드 전용(차트 값에는 적용 안 함).
  const pivotValueFormatter = (params) => {
    const { value } = params;
    if (value === null || value === undefined || !Number.isFinite(value)) return "";
    if (value === 0) return "";
    const formatted = NumberUtil.formatNumberData(params, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
    return params.data?.analysisIndicator === "FP_MAX_CONSUME_RATE" ? `${formatted}%` : formatted;
  };

  // 핵심 지표(최대 투입율) 강조 — old는 text-font-bold(굵게)였으나, FP Report 전반의 지표 강조
  // 관례(green[50] 배경만, 굵게는 안 씀 — MaterialPsi/PsiDetail/ProdSalesStockStatus/
  // ResourceUtilization과 동일)로 통일한다(2026-08-12 사용자 지시). 분석 지표 컬럼~피벗 컬럼에만
  // 적용하고 앞쪽 고정 컬럼(공장/스테이지/툴자원)에는 걸지 않는다.
  const analysisIndicatorHighlightStyle = (params) => (
    params.data?.analysisIndicator === "FP_MAX_CONSUME_RATE" ? { backgroundColor: green[50] } : null
  );
  const pivotCellStyle = analysisIndicatorHighlightStyle;

  // 고정(왼쪽 pinned) 컬럼 — old fixColumns(plantCd/plantNm/toolResourceCd/toolResourceNm/stageCd/
  // stageNm/analysisIndicator)와 1:1(순서는 old gridItems의 실제 시각적 배치: 공장>스테이지>툴자원).
  const fixedColumnDefs = useMemo(() => [
    ...GROUP_FIELDS.map(field => ({
      headerName: field, field, colId: field, rowGroup: true, hide: true, suppressColumnsToolPanel: true
    })),

    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, PLANT_NM_HIDE_KEY),
      cdColId: PLANT_CD_COL_ID, cdField: "plantCd", cdHeader: "FP_PLANT_CD",
      nmColId: PLANT_NM_COL_ID, nmField: "plantNm", nmHeader: "PLANT_NM",
      hideKey: PLANT_NM_HIDE_KEY, width: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, STAGE_NM_HIDE_KEY),
      cdColId: STAGE_CD_COL_ID, cdField: "stageCd", cdHeader: "FP_STAGE_CD",
      nmColId: STAGE_NM_COL_ID, nmField: "stageNm", nmHeader: "FP_STAGE_NM",
      hideKey: STAGE_NM_HIDE_KEY, width: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, TOOL_RESOURCE_NM_HIDE_KEY),
      cdColId: TOOL_RESOURCE_CD_COL_ID, cdField: "toolResourceCd", cdHeader: "FP_TOOL_RESOURCE_CD",
      nmColId: TOOL_RESOURCE_NM_COL_ID, nmField: "toolResourceNm", nmHeader: "FP_TOOL_RESOURCE_NM",
      hideKey: TOOL_RESOURCE_NM_HIDE_KEY, width: 150
    }),

    // leaf(분석 지표) — 트리의 가장 안쪽 실제 행(평범한 field 컬럼, ResourceUtilization의
    // analysisIndicator와 동일 자리).
    {
      headerName: "FP_ANALYSIS_INDICATOR", field: "analysisIndicator", colId: "analysisIndicator", width: 150,
      pinned: "left", lockPinned: true, type: ["nonEditableColumn"], valueFormatter: analysisIndicatorFormatter,
      cellStyle: analysisIndicatorHighlightStyle
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  // 요일 헤더 번역+토/일 강조색은 common/grid.js buildDayBucketPivotColumnDefs가 처리한다
  // (§A-6d/§A-6f). pivotValueFormatter/pivotCellStyle은 이 화면 고유(FP_MAX_CONSUME_RATE 행
  // '%' suffix+굵게, 0값 공백)이라 childExtraProps로 얹는다.
  const buildPivotColumnDefs = (crosstabHeader) => buildDayBucketPivotColumnDefs(crosstabHeader, t, {
    childExtraProps: () => ({ valueFormatter: pivotValueFormatter, cellStyle: pivotCellStyle })
  });

  // old setCrossTabGridDataWithBucket — pivotData[i]를 crosstabHeader[i] 필드로 펼친다.
  // 백엔드가 내려주는 6개 지표를 전부 표시한다 — 이관 당시 FP_TOTAL_AVAIL_CNT(총 가용 개수)를
  // "old에 없는 지표"로 보고 걸러냈으나, old 화면에도 실제로 있는 지표임이 확인돼 필터를 제거했다
  // (2026-08-12 사용자 확인).
  const buildRowData = (crosstabHeader, data) => (data ?? [])
    .map(row => {
      const flat = {
        plantCd: row.plantCd, plantNm: row.plantNm,
        stageCd: row.stageCd, stageNm: row.stageNm,
        toolResourceCd: row.toolResourceCd, toolResourceNm: row.toolResourceNm,
        analysisIndicator: row.analysisIndicator
      };
      (crosstabHeader ?? []).forEach((header, index) => { flat[header] = row.pivotData?.[index]; });
      return flat;
    });

  const toParamDate = (date) => (date ? format(date, PARAM_DATE_FORMAT) : null);

  // old: resourceCdOptionsRef.current?.length === getValues('toolResourceCd')?.length이면 빈 배열
  // (필터 없음=전체)로 보낸다 — "전체 선택"과 "필터 없음"을 동일하게 취급.
  const computeToolResourceCds = () => {
    const { selected, options } = searchStateRef.current.toolResourceState;
    return selected.length === options.length ? [] : selected;
  };

  // 조회/차트 공통 파라미터(old searchParams의 공통부) — toolResourceCds는 old가 loadData/
  // loadChartData 양쪽에 모두 실어 보내던 필터라 여기서 공통으로 둔다(§A-2/§A-9b 디바운스 참고).
  const buildBaseParams = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;
    return {
      viewId: viewName,
      subId: "",
      planScope: version.planScope,
      versionCd: version.versionCd,
      toolResourceCds: computeToolResourceCds(),
      bucketType: period.bucketType,
      fromDt: toParamDate(period.startDate),
      toDt: toParamDate(period.endDate),
      usePlanPeriod: !!period.isEntirePlanPeriod
    };
  };

  const clearChart = () => {
    prevChartParamsRef.current = null;
    setChartLabels([]);
    setMainChartData([]);
    setMainChartSeries([]);
    setSubChartData([]);
    setSubChartSeries([]);
    getFilterStore(viewName).clear();
  };

  // 하단 상태바 브레드크럼 — ResourceUtilization과 동일한 슬롯 매핑(공장→originLoc, 스테이지→
  // destLoc, 툴자원→item — FilterStatusPanel의 4개 순서 슬롯 중 1·2·4번).
  const updateStatusBreadcrumb = (target) => {
    getFilterStore(viewName).setValue({
      originLocCd: target.plantCd ?? null, originLocCdHeader: t("FP_PLANT_CD"),
      originLocNm: target.plantNm ?? null, originLocNmHeader: t("PLANT_NM"),
      destLocCd: target.stageCd ?? null, destLocCdHeader: t("FP_STAGE_CD"),
      destLocNm: target.stageNm ?? null, destLocNmHeader: t("FP_STAGE_NM"),
      itemCd: target.toolResourceCd ?? null, itemCdHeader: t("FP_TOOL_RESOURCE_CD"),
      itemNm: target.toolResourceNm ?? null, itemNmHeader: t("FP_TOOL_RESOURCE_NM")
    });
  };

  const leftAxisLabel = useMemo(() => `(${t("QTY")})`, [t]);
  const rightAxisLabel = useMemo(() => `${t("FP_MAX_CONSUME_RATE")} (%)`, [t]);

  // old makeChartDataset — analysisIndicator별로 한 행(long-format, PivotChartComposed의
  // groupKey="analysisIndicator"가 자동으로 범례/시리즈를 생성).
  const buildChartPoints = (rows, chartHeader) => rows.map(row => {
    const point = { analysisIndicator: row.analysisIndicator };
    chartHeader.forEach((header, index) => { point[header] = row.pivotData?.[index] ?? 0; });
    return point;
  });

  const buildChartSeries = (rows) => rows.map(row => {
    const conf = INDICATOR_CONFIG[row.analysisIndicator];
    const isLine = conf.type === "line";
    return {
      field: row.analysisIndicator, text: row.analysisIndicator, type: conf.type,
      groupName: conf.orientation === "right" ? rightAxisLabel : leftAxisLabel,
      orientation: conf.orientation,
      typeOptions: isLine
        ? { stroke: conf.color, name: t(row.analysisIndicator) }
        : { fill: conf.color, stackId: conf.stackId, name: t(row.analysisIndicator) }
    };
  });

  // old hasValue — MUST_INDICATORS(최대투입/최소잔여)는 항상 시리즈를 만들고, 나머지는 버킷
  // 전체가 0/null이면 시리즈 자체를 만들지 않는다.
  const hasChartValue = (indicator, pivotData) => (
    MUST_INDICATORS.has(indicator) || (pivotData ?? []).some(v => v !== null && v !== undefined && v !== 0)
  );

  // 클릭 기준으로 차트를 다시 조회한다. target = { plantCd, plantNm, stageCd, stageNm,
  // toolResourceCd, toolResourceNm }(상위 레벨을 클릭하면 하위 코드는 없음, old updateChartFromGrid와 동일).
  const loadChart = (target) => {
    const { versionState: version } = searchStateRef.current;
    if (!version.planScope || !version.versionCd) return;

    updateStatusBreadcrumb(target);

    const nextParams = {
      plantCd: target.plantCd ?? "", stageCd: target.stageCd ?? "", toolResourceCd: target.toolResourceCd ?? ""
    };
    const prev = prevChartParamsRef.current;
    if (prev && prev.plantCd === nextParams.plantCd && prev.stageCd === nextParams.stageCd
      && prev.toolResourceCd === nextParams.toolResourceCd) {
      return;
    }
    prevChartParamsRef.current = nextParams;

    const reqId = ++latestChartReqRef.current;
    // old loadChartData는 useGrouping:'true'(그리드 조회의 false와 반대) — 드릴다운 레벨(plant/
    // stage/toolResource 단위)로 백엔드가 집계해서 내려준다(ToolAvailabilityCrosstabDto.merge 참고).
    const params = { ...buildBaseParams(), ...nextParams, useGrouping: true };

    // TODO(MIG-T2): [toolavailability/ToolAvailability] 그리드 컬럼 필터(값 목록 체크박스) 미이관.
    //   old: plantCd/stageCd/toolResourceCd에 RealGrid autoFilter, 필터 선택값을 차트 조회의
    //        changes(FilterParameter)로 함께 전송
    //   사유: 행 그룹(트리)으로 전환하면서 코드 컬럼이 hide:true인 데이터 컬럼이 돼 헤더 필터
    //        아이콘을 띄울 자리가 없어졌다. 백엔드는 changes를 계속 받으므로 2차에 트리거 UI만
    //        얹으면 된다(가이드 §A-6e와 동일한 결정) — 지금은 빈 필터로 보낸다.
    fpToolAvailabilityService.getToolAvailabilityChart(params, {})
      .then(response => {
        if (reqId !== latestChartReqRef.current) return;

        const { chartHeader, data } = response.data ?? {};
        // 차트는 MAIN_INDICATORS/SUB_INDICATORS에 정의된 5개 지표만 그린다(총 가용 개수는 그리드
        // 전용 — old 차트에도 없다). 정의에 없는 지표는 아래 시리즈 구성에서 자연스럽게 빠진다.
        const rows = (data ?? []).filter(r => hasChartValue(r.analysisIndicator, r.pivotData));

        if (isEmpty(rows) || isEmpty(chartHeader)) {
          setChartLabels([]);
          setMainChartData([]);
          setMainChartSeries([]);
          setSubChartData([]);
          setSubChartSeries([]);
          return;
        }

        const mainRows = rows.filter(r => MAIN_INDICATORS.includes(r.analysisIndicator));
        const subRows = rows.filter(r => SUB_INDICATORS.includes(r.analysisIndicator));

        setChartLabels(chartHeader);
        setMainChartData(buildChartPoints(mainRows, chartHeader));
        setMainChartSeries(buildChartSeries(mainRows));
        setSubChartData(buildChartPoints(subRows, chartHeader));
        setSubChartSeries(buildChartSeries(subRows));
      })
      .catch(e => {
        prevChartParamsRef.current = null;
        debug(e);
      });
  };

  const retrieveToolAvailability = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;

    if (!version.planScope || !version.versionCd) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_VERSION") });
      return;
    }
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_PLAN_PERIOD") });
      return;
    }

    const reqId = ++latestGridReqRef.current;
    // old loadData — useGrouping:false로 명시(공장/스테이지/툴자원/지표별 세부 행 전부 요청).
    const params = { ...buildBaseParams(), plantCds: version.plantCd, useGrouping: false };

    gridRef.current?.api?.setGridOption("loading", true);

    fpToolAvailabilityService.getToolAvailabilities(params)
      .then(response => {
        if (reqId !== latestGridReqRef.current) return;

        const { crosstabHeader, data } = response.data ?? {};
        const api = gridRef.current?.api;
        if (!api) return;

        const nextColumnDefs = [...fixedColumnDefs, ...buildPivotColumnDefs(crosstabHeader)];
        GridUtils.setColumnDefs({ api, columnDefs: nextColumnDefs, viewName, initState: false });
        applyColumnOrder(api, nextColumnDefs);

        const rows = buildRowData(crosstabHeader, data);
        setRowData(rows);

        if (isEmpty(rows)) {
          clearChart();
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          return;
        }

        // old: 조회 직후 첫 행의 공장 기준으로 차트를 그리고, 그리드의 선택 셀도 그 행으로 옮긴다.
        pendingFocusRef.current = { rowIndex: 0, colId: PLANT_CD_COL_ID };
        prevChartParamsRef.current = null;
        loadChart({ plantCd: rows[0].plantCd, plantNm: rows[0].plantNm });
      })
      .catch(e => {
        if (reqId === latestGridReqRef.current) clearChart();
        debug(e);
      })
      .finally(() => {
        if (reqId === latestGridReqRef.current) gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs: fixedColumnDefs, viewName, initState: false });
  };

  const onRowDataUpdated = () => consumePendingFocus(gridRef, pendingFocusRef);

  const PLANT_LEVEL_COL_IDS = new Set([PLANT_CD_COL_ID, PLANT_NM_COL_ID]);
  const STAGE_LEVEL_COL_IDS = new Set([STAGE_CD_COL_ID, STAGE_NM_COL_ID]);

  // 클릭한 셀에서 차트를 그릴 드릴다운 대상을 뽑는다(old updateChartFromGrid의
  // `column.startsWith('plant'|'stage')` 분기를 트리 표시 컬럼 colId 기준으로 옮긴 것).
  const resolveChartTarget = (params) => {
    const node = params.node;
    if (!node) return null;

    if (node.group) {
      const target = {};
      for (let current = node; current; current = current.parent) {
        if (current.field) target[current.field] = current.key;
      }
      return isEmpty(target) ? null : target;
    }

    const row = node.data;
    if (!row) return null;

    const colId = params.column?.getColId() ?? "";
    if (PLANT_LEVEL_COL_IDS.has(colId)) {
      return { plantCd: row.plantCd, plantNm: row.plantNm };
    }
    if (STAGE_LEVEL_COL_IDS.has(colId)) {
      return { plantCd: row.plantCd, plantNm: row.plantNm, stageCd: row.stageCd, stageNm: row.stageNm };
    }
    return {
      plantCd: row.plantCd, plantNm: row.plantNm, stageCd: row.stageCd, stageNm: row.stageNm,
      toolResourceCd: row.toolResourceCd, toolResourceNm: row.toolResourceNm
    };
  };

  const onCellClicked = (params) => {
    const target = resolveChartTarget(params);
    if (target) loadChart(target);
  };

  const statusBar = useMemo(() => buildReportStatusBar(viewName), [viewName]);

  // 툴 자원 멀티셀렉트 옵션 — old loadResources(handleChange({planScope}) 트리거) 대응.
  // planScope가 바뀔 때마다 재조회하고, 로드 즉시 "전체 선택"으로 기본값을 채운다(old setValue).
  useEffect(() => {
    if (!versionState.planScope) {
      setToolResourceState({ selected: [], options: [] });
      return;
    }

    let cancelled = false;
    const reqId = ++latestResourceReqRef.current;

    fpCommonService.getToolResources({ viewId: viewName, subId: "", planScope: versionState.planScope, isTool: true })
      .then(response => {
        if (cancelled || reqId !== latestResourceReqRef.current) return;
        const options = (response.data ?? []).map(r => ({ value: r.resourceCd, label: r.resourceNm }));
        setToolResourceState({ selected: options.map(o => o.value), options });
      })
      .catch(e => debug(e));

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.planScope]);

  // 버전이 바뀌면 조회 구간을 다시 계산한다(old setVersionPeriod) — PlanResult.js와 동일 원리.
  useEffect(() => {
    const version = versionState.selectedVersion;
    if (!version?.startTs) {
      setPeriodState(prev => (prev.startDate || prev.endDate) ? { ...prev, startDate: null, endDate: null } : prev);
      return;
    }

    const startDate = new Date(version.startTs);
    const endDate = addDays(startDate, SEARCH_ZONE_DAYS);
    setPeriodState(prev => ({ ...prev, startDate, endDate }));

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.selectedVersion]);

  useEffect(() => {
    if (versionState.versionCd) return;

    debouncedRetrieveRef.current.cancel();

    const api = gridRef.current?.api;
    if (api) {
      GridUtils.setColumnDefs({ api, columnDefs: fixedColumnDefs, viewName, initState: false });
      applyColumnOrder(api, fixedColumnDefs);
    }
    setRowData([]);
    clearChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd]);

  // 선택형 조건(버전/plant/기간/버킷/툴자원 선택)이 바뀌면 값이 준비되는 즉시 자동 조회한다
  // (§A-9b). toolResourceState.selected는 old의 멀티셀렉트에 대응하는 선택형 조건이라 여기 포함.
  useEffect(() => {
    const version = searchStateRef.current.versionState;
    const period = searchStateRef.current.periodState;
    if (!version.planScope || !version.versionCd) return;
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) return;

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    periodState.startDate, periodState.endDate, periodState.bucketType, periodState.isEntirePlanPeriod,
    versionState.plantCd, toolResourceState.selected
  ]);

  // old ToolAvailabilityChart.jsx는 두 차트 모두 plugins에 ChartLegendPlugin을 실제로 포함시켜
  // 범례를 보여줬다(§A-7 "ChartLegendPlugin 판단 시 주의") → recharts 기본 <Legend/>를 CSS로
  // 숨기지 않고 그대로 노출한다. 클릭 시 series 숨김+취소선(old ChartLegendPlugin의 인터랙션)은 Tier2.
  const mainPivotConfig = useMemo(() => ({
    groupKey: "analysisIndicator",
    chartSeries: mainChartSeries,
    include: chartLabels,
    xAxisSortMap: buildXAxisSortMap(chartLabels)
  }), [mainChartSeries, chartLabels]);

  const subPivotConfig = useMemo(() => ({
    groupKey: "analysisIndicator",
    chartSeries: subChartSeries,
    include: chartLabels,
    xAxisSortMap: buildXAxisSortMap(chartLabels)
  }), [subChartSeries, chartLabels]);

  return (
    <>
      <FilterContainer>
        <VersionSearchGroup
          value={versionState}
          onChange={(partial) => setVersionState(prev => ({ ...prev, ...partial }))}
        />
        <PeriodSearchGroup
          value={periodState}
          onChange={(partial) => setPeriodState(prev => ({ ...prev, ...partial }))}
          bucketCase={TOOL_AVAILABILITY_BUCKET_CASE}
        />
        {/* old InputField(type="multiSelect", toolResourceCd) — controlled MUI Autocomplete(multiple)
            로 재현(§A-1 방침). 조회 버튼 없음(§A-9b) — 선택형 조건이라 값이 바뀌는 즉시 자동조회된다. */}
        <Autocomplete
          multiple
          size="small"
          options={toolResourceState.options}
          getOptionLabel={(o) => o?.label ?? ""}
          isOptionEqualToValue={(o, v) => o.value === v.value}
          value={toolResourceState.options.filter(o => toolResourceState.selected.includes(o.value))}
          onChange={(_, options) => setToolResourceState(prev => ({ ...prev, selected: options.map(o => o.value) }))}
          renderInput={(params) => <TextField {...params} label={t("FP_RESOURCE")} />}
          sx={{ ml: 1.5, minWidth: 250 }}
        />
        <Box sx={{ display: "flex" }}>
          <DownloadButton
            onClick={() => gridRef.current?.api?.exportDataAsExcel({ fileName: `${t(title)}.xlsx`, sheetName: t(title) })}
          />
        </Box>
      </FilterContainer>
      <SplitterContainer initialSizes={[1, 1]}>
        <Box id="fp-tool-availability-grid" className="ag-theme-balham grid-area" sx={{ height: "calc(100% - 8px)", pb: 1 }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            {...defaultGridMemo}
            statusBar={statusBar}
            onGridReady={onGridReady}
            onRowDataUpdated={onRowDataUpdated}
            onCellClicked={onCellClicked}
          />
        </Box>
        <Box className="chart-area" sx={{ height: "calc(100% - 8px)", mt: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* old ToolAvailabilityChart.jsx의 토글 버튼(FP_DISPLAY_SUPPLY_DISPOSAL_CHART, 기본 켜짐) —
              메인 차트(최대투입율/최대투입량/최소잔여량)와 서브 차트(공급/폐기 개수)를 세로 배치하고,
              서브 차트를 조건부 렌더링으로 표시/숨김한다(그리드처럼 보존해야 할 dirty state가 없어
              §11-1의 display 토글이 아니라 조건부 마운트로도 무방 — 사용자 결정, Tier1 구현). */}
          <ToggleButton
            value="supplyDisposal"
            selected={showSupplyDisposalChart}
            onChange={() => setShowSupplyDisposalChart(prev => !prev)}
            size="small"
            color="primary"
            sx={{ alignSelf: "flex-end", gap: 0.5, fontSize: 12, textTransform: "none", padding: "4px", flexShrink: 0, mb: 0.5 }}
          >
            <BarChartIcon sx={{ fontSize: 16 }} />
            {t("FP_DISPLAY_SUPPLY_DISPOSAL_CHART")}
          </ToggleButton>
          <Box sx={{ flex: showSupplyDisposalChart ? 3 : 1, minHeight: 0, overflow: "hidden" }}>
            {/* TODO(MIG-T2): [toolavailability/ToolAvailability] 차트 장식성 인터랙션 3종 미이관.
                  old: ChartHatchPatternPlugin(빗금), highlightChartByLabel/resetChartHighlighting(hover dim),
                       ChartDataLabelPlugin(막대 위 수치)
                  사유: recharts/PivotChartComposed에 대응 훅이 없고, 1차 목표(조회·표시)에 불필요 — 가이드 §A-7에서 Tier2 확정 */}
            <PivotChartComposed
              pivotConfig={mainPivotConfig}
              data={mainChartData}
              options={{ tooltipContentStyle: { fontSize: "12px" }, minHeight: "0px", decimalPoint: true }}
              showConfigurationPopup={false}
              nullConvert
            />
          </Box>
          {showSupplyDisposalChart && (
            <Box sx={{ flex: 2, minHeight: 0, overflow: "hidden", mt: 1 }}>
              <PivotChartComposed
                pivotConfig={subPivotConfig}
                data={subChartData}
                options={{ tooltipContentStyle: { fontSize: "12px" }, minHeight: "0px", decimalPoint: true }}
                showConfigurationPopup={false}
                nullConvert
              />
            </Box>
          )}
        </Box>
      </SplitterContainer>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(ToolAvailability);
